import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { looksLikeProse, type Id } from '@odhaczka/core';
import { articleOptions, useArticleIndex } from '../../data/articles';
import { aiDisabled } from '../../data/ai';
import { aiCandidatesFor, analyzeText, withAi, type AiCandidates, type DraftItem, type Resolution } from '../../data/products';
import type { ArticleRow } from '../../db/types';
import { useSyncStatus } from '../../sync/useSyncStatus';

export const asksAbout = (item: DraftItem) => !item.exact && item.candidates.length > 0;

/** Pytanie AI o te same produkty startuje chwilę po ostatniej zmianie tekstu. */
const AI_DELAY_MS = 1000;

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export interface Draft {
  /** Pozycje z podglądu (z opóźnieniem ~250 ms względem tekstu). */
  items: DraftItem[];
  /** Pytania „to samo co…?” bez odpowiedzi. */
  unanswered: number;
  /** Trwa pytanie AI o te same produkty pod inną nazwą. */
  aiPending: boolean;
  answerFor: (item: DraftItem) => Resolution | undefined;
  answer: (key: string, a: Resolution | null) => void;
  /** Konkretne towary z paragonów do przypięcia (pusta lista, gdy podpowiedzi wyłączone). */
  articlesFor: (item: DraftItem) => ArticleRow[];
  pinnedFor: (item: DraftItem) => Id | undefined;
  pin: (key: string, articleId: Id | null) => void;
  /** Pozycje i decyzje dla bieżącego tekstu — gotowe dla createList/addItems. */
  finalize: () => Promise<{ items: DraftItem[]; resolutions: Record<number, Resolution>; pins: Record<number, Id> }>;
}

/**
 * Rozbiór wklejonego tekstu z podglądem na żywo i odpowiedziami na pytania o podobne produkty (offline, AI jako dodatek).
 * single = tekst to jedna pozycja (edycja), przecinki nie dzielą go na kilka.
 */
export function useDraft(text: string, single = false, opts: { articles?: boolean } = {}): Draft {
  // Odpowiedzi po kluczu produktu, żeby przetrwały edycję tekstu (indeksy linii się przesuwają).
  const [answers, setAnswers] = useState<Record<string, Resolution>>({});
  // Przypięte artykuły po kluczu produktu (jak odpowiedzi — przetrwają edycję tekstu).
  const [pins, setPins] = useState<Record<string, Id>>({});
  const articleIndex = useArticleIndex(!!opts.articles);
  // Kandydaci od AI poza analizą tekstu: useLiveQuery i finalize analizują tekst od nowa.
  const [ai, setAi] = useState<AiCandidates>({});
  const [aiPending, setAiPending] = useState(false);
  const inflight = useRef<{ sig: string; ctrl: AbortController } | null>(null);
  // Powrót zasięgu ponawia pytanie o klucze, o które nie udało się zapytać.
  const online = useSyncStatus().online;
  const debounced = useDebounced(text, 250);
  const aiText = useDebounced(text, AI_DELAY_MS);
  const analysis = useLiveQuery(
    async () => ({ text: debounced, items: debounced.trim() ? await analyzeText(debounced, single) : [] }),
    [debounced, single],
  );
  const items = useMemo(() => withAi(analysis?.items ?? [], ai), [analysis, ai]);

  // Wynik AI tylko dla kluczy bez odpowiedzi w chwili nadejścia: wcześniejsze „Nie, inny produkt”
  // zapisałoby link „different” także dla kandydata, którego użytkownik nie widział.
  const storeAi = useEffectEvent((found: AiCandidates) =>
    setAi((prev) => {
      const next = { ...prev };
      for (const [key, c] of Object.entries(found)) next[key] = Object.hasOwn(answers, key) ? null : c;
      return next;
    }),
  );

  // Klucz z odpowiedzią sprzed startu zapytania i tak by przepadł (storeAi), więc go nie wysyłamy.
  const answered = useEffectEvent((key: string) => Object.hasOwn(answers, key));

  useEffect(() => {
    if (!analysis || analysis.text !== aiText) return;
    // Wiadomość zdaniami najpierw do rozpisania (ProseAssist) — pytanie o jej surowe linie byłoby zbędne.
    const prose = looksLikeProse(analysis.text);
    // Pytamy raz w szkicu o każdy klucz bez dokładnego dopasowania (także gdy AI nic nie znalazło).
    const ask = prose
      ? []
      : [...new Map(analysis.items.filter((i) => i.key && !i.exact && !Object.hasOwn(ai, i.key) && !answered(i.key)).map((i) => [i.key, i])).values()];
    const sig = ask.map((i) => i.key).join('\n');
    if (inflight.current?.sig === sig) return;
    if (inflight.current) {
      // Tekst zmienił zestaw pytań — poprzednie zapytanie jest nieaktualne.
      inflight.current.ctrl.abort();
      inflight.current = null;
      setAiPending(false);
    }
    // Offline albo serwer bez AI: nie pytamy (i nie migamy „sprawdzam podobne…”).
    if (ask.length === 0 || !online || aiDisabled()) return;
    const ctrl = new AbortController();
    inflight.current = { sig, ctrl };
    setAiPending(true);
    void aiCandidatesFor(ask, ctrl.signal)
      .catch(() => null)
      .then((found) => {
        if (ctrl.signal.aborted) return;
        inflight.current = null;
        setAiPending(false);
        // null = AI niedostępne (offline, błąd) — spróbujemy przy kolejnej zmianie.
        if (found) storeAi(found);
      });
  }, [analysis, aiText, ai, online]);

  useEffect(() => () => inflight.current?.ctrl.abort(), []);

  const answerFor = (item: DraftItem): Resolution | undefined => {
    const a = answers[item.key];
    // Odpowiedź „to samo” ważna tylko, jeśli ten kandydat nadal jest na liście.
    if (a?.kind === 'same' && !item.candidates.some((c) => c.productId === a.productId)) return undefined;
    return a;
  };

  const answer = (key: string, a: Resolution | null) =>
    setAnswers((prev) => {
      const next = { ...prev };
      if (a) next[key] = a;
      else delete next[key];
      return next;
    });

  const articlesFor = (item: DraftItem): ArticleRow[] => {
    if (!articleIndex || !opts.articles || !item.key) return [];
    const a = answerFor(item);
    return articleOptions(articleIndex, item.key, item.exact ?? (a?.kind === 'same' ? a.productId : null));
  };
  const pinnedFor = (item: DraftItem) => (opts.articles ? pins[item.key] : undefined);
  const pin = (key: string, articleId: Id | null) =>
    setPins((prev) => {
      const next = { ...prev };
      if (articleId) next[key] = articleId;
      else delete next[key];
      return next;
    });

  const finalize = async () => {
    // Podgląd może być sprzed chwili (debounce) — wtedy analizujemy bieżący tekst. Nie czekamy na AI.
    const raw = analysis?.text === text ? analysis.items : text.trim() ? await analyzeText(text, single) : [];
    const current = withAi(raw, ai);
    const resolutions: Record<number, Resolution> = {};
    const pinned: Record<number, Id> = {};
    current.forEach((item, i) => {
      const a = asksAbout(item) ? answerFor(item) : undefined;
      if (a) resolutions[i] = a;
      const p = pinnedFor(item);
      if (p) pinned[i] = p;
    });
    return { items: current, resolutions, pins: pinned };
  };

  return {
    items,
    unanswered: items.filter((i) => asksAbout(i) && !answerFor(i)).length,
    aiPending,
    answerFor,
    answer,
    articlesFor,
    pinnedFor,
    pin,
    finalize,
  };
}
